import { debug, error } from '@/utils/logger';
import {
    clientDeleteImage,
    clientUploadGenericImage,
    clientUploadHeroImage,
    clientUploadProfileImage,
} from '@/lib/upload/clientUpload';

export const uploadFile = async (file: File, path?: string): Promise<string | null> => {
    try {
        const { url } = await clientUploadGenericImage(file, path);
        return url || null;
    } catch (err) {
        error('Error uploading file:', err);
        throw err;
    }
};

export const uploadProfilePicture = async (
    file: File,
    userId?: string,
): Promise<{ large_avatar_url: string; small_avatar_url: string }> => {
    try {
        debug('[uploadProfilePicture] Starting profile picture upload', {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            userId: userId || '(not provided)',
        });
        const result = await clientUploadProfileImage(file, userId);
        debug('[uploadProfilePicture] Profile picture upload successful', result);
        return result;
    } catch (err) {
        error('[uploadProfilePicture] Error uploading profile picture:', err);
        throw err;
    }
};

export const uploadHeroImage = async (
    file: File,
    userId?: string,
    path?: string,
): Promise<{ hero_image_url: string }> => {
    try {
        debug('[uploadHeroImage] Starting hero image upload', {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            userId: userId || '(not provided)',
            path: path || '(not provided)',
        });
        const result = await clientUploadHeroImage(file, userId, path);
        debug('[uploadHeroImage] Hero image upload successful', result);
        return result;
    } catch (err) {
        error('[uploadHeroImage] Error uploading hero image:', err);
        throw err;
    }
};

export async function uploadPostCoverPhoto(
    file: File,
    postId?: string,
): Promise<{ hero_image_url: string }> {
    try {
        const timestamp = Date.now();
        const fileName = postId ? `cover_${postId}_${timestamp}.jpg` : `cover_${timestamp}.jpg`;
        const path = `public/post_images/large/${fileName}`;
        return uploadHeroImage(file, undefined, path);
    } catch (err) {
        error('[uploadPostCoverPhoto] Error uploading post cover photo:', err);
        throw err;
    }
}

export async function uploadPlacePhoto(file: File, postLocationId: string): Promise<string> {
    try {
        const timestamp = Date.now();
        const fileName = `place_${postLocationId}_${timestamp}.jpg`;
        const path = `public/post_location_images/large/${fileName}`;
        const result = await uploadHeroImage(file, undefined, path);
        return result.hero_image_url;
    } catch (err) {
        error('[uploadPlacePhoto] Error uploading place photo:', err);
        throw err;
    }
}

export async function uploadPlacePhotos(files: File[], postLocationId: string): Promise<string[]> {
    try {
        const uploadPromises = files.map((file) => uploadPlacePhoto(file, postLocationId));
        return await Promise.all(uploadPromises);
    } catch (err) {
        error('[uploadPlacePhotos] Error uploading place photos:', err);
        throw err;
    }
}

export async function uploadCardImage(
    file: File,
    entityType: 'destination' | 'travel_style',
    entityId: string,
): Promise<string> {
    const timestamp = Date.now();
    const fileName = `${entityId}_${timestamp}.jpg`;
    const path = `public/card_images/${entityType}s/${fileName}`;
    const result = await uploadHeroImage(file, undefined, path);
    return result.hero_image_url;
}

export async function uploadRecommendationPhoto(file: File, locationId: string): Promise<string> {
    try {
        const timestamp = Date.now();
        const fileName = `rec_${locationId}_${timestamp}.jpg`;
        const path = `public/recommendation_images/large/${fileName}`;
        const result = await uploadHeroImage(file, undefined, path);
        return result.hero_image_url;
    } catch (err) {
        error('[uploadRecommendationPhoto] Error uploading recommendation photo:', err);
        throw err;
    }
}

export async function deleteS3Image(imageUrl: string): Promise<void> {
    try {
        await clientDeleteImage(imageUrl);
    } catch (err) {
        error('[deleteS3Image] Error deleting S3 image:', err);
        throw err;
    }
}
